import { Head, router } from "@inertiajs/react"
import { Pencil, Plus, ShieldOff, Trash2 } from "lucide-react"
import { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"

import { AuditHistoryPanel } from "@/components/admin/audit-history-panel"
import { BlocklistFormDialog } from "@/components/admin/blocklist-form-dialog"
import { ListPagination } from "@/components/admin/list-pagination"
import { ListShell } from "@/components/admin/list-shell"
import { EmptyState } from "@/components/empty-state"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { useOnline } from "@/hooks/use-online"
import AppLayout from "@/layouts/app-layout"
import { adminBlocklistEntriesPath, adminBlocklistEntryPath } from "@/routes"
import type { BlocklistEntry, BlocklistIndexProps } from "@/types/admin"

/**
 * Admin/Blocklist/Index — global TikTok-handle blocklist (OKS-15
 * AC6-AC8 + AC7 audit panel + UX Spec v2.1 §3 F4 duplicate hot-swap).
 *
 * Composition:
 *   - `<ListShell>` chrome with the locked `admin-blocklist` page testid
 *   - "Add handle" primary action → opens `<BlocklistFormDialog mode=add>`
 *   - Entries table: Handle · Reason · Added by · Added · [Edit][Remove]
 *   - `<ListPagination>` footer for entries
 *   - `<AuditHistoryPanel>` collapsible (default-collapsed) below — its
 *     own `events_page` URL param drives independent pagination
 *   - `<BlocklistFormDialog mode=overwrite>` mounts when Backend ships
 *     the `duplicate_found` prop (server-told hot-swap)
 *   - `<RemoveConfirmDialog>` (inline) — destructive shadcn `Dialog`
 *     with Cancel autoFocused per UX Spec §5
 *
 * Admin/Blocklist is purely server-side state — every mutation lands a
 * full reload via Backend's 303 redirect (or 200 same-page render for
 * the duplicate_found path). No optimistic UI.
 */
export default function AdminBlocklistIndex({
  entries,
  pagination,
  events,
  events_pagination,
  duplicate_found,
}: BlocklistIndexProps) {
  const { t, i18n } = useTranslation()
  const online = useOnline()

  const dateFmt = new Intl.DateTimeFormat(i18n.language, {
    year: "numeric",
    month: "short",
    day: "numeric",
  })

  // Server-told overwrite mount — `duplicate_found` arrives on the
  // Inertia props after a `POST /admin/blocklist` collision. Track
  // local "should render the overwrite dialog?" state so we can
  // dismiss without forcing a re-fetch.
  const [overwriteOpen, setOverwriteOpen] = useState(Boolean(duplicate_found))
  useEffect(() => {
    setOverwriteOpen(Boolean(duplicate_found))
  }, [duplicate_found])

  const onEntriesPageChange = (page: number) => {
    router.get(
      adminBlocklistEntriesPath(),
      { page },
      {
        preserveState: true,
        preserveScroll: true,
        only: ["entries", "pagination"],
      },
    )
  }

  const onEventsPageChange = (page: number) => {
    router.get(
      adminBlocklistEntriesPath(),
      { events_page: page },
      {
        preserveState: true,
        preserveScroll: true,
        only: ["events", "events_pagination"],
      },
    )
  }

  return (
    <AppLayout>
      <Head title={t("admin.blocklist.title")} />
      <ListShell
        testId="admin-blocklist"
        title={t("admin.blocklist.title")}
        subtitle={t("admin.blocklist.subtitle_total", {
          count: pagination.total,
        })}
        action={
          <BlocklistFormDialog
            mode={{
              kind: "add",
              trigger: (
                <Button
                  type="button"
                  disabled={!online}
                  aria-disabled={!online ? "true" : undefined}
                  data-testid="admin-blocklist-add-button"
                >
                  <Plus className="size-4" aria-hidden="true" />
                  {t("admin.blocklist.add_button")}
                </Button>
              ),
            }}
          />
        }
        footer={
          <>
            <ListPagination
              page={pagination.page}
              perPage={pagination.per_page}
              total={pagination.total}
              onPageChange={onEntriesPageChange}
            />
            <AuditHistoryPanel
              events={events}
              pagination={events_pagination}
              onPageChange={onEventsPageChange}
            />
          </>
        }
      >
        {entries.length === 0 ? (
          <EmptyState
            data-testid="admin-list-empty"
            icon={ShieldOff}
            title={t("admin.list.empty_fresh_blocklist")}
          />
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t("admin.blocklist.column.handle")}</TableHead>
                <TableHead>{t("admin.blocklist.column.reason")}</TableHead>
                <TableHead>{t("admin.blocklist.column.added_by")}</TableHead>
                <TableHead>{t("admin.blocklist.column.added_at")}</TableHead>
                <TableHead className="w-32 text-right">
                  <span className="sr-only">{t("admin.shared.actions")}</span>
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {entries.map((entry) => (
                <TableRow
                  key={entry.id}
                  data-testid="admin-blocklist-row"
                  data-entry-id={entry.id}
                >
                  <TableCell className="font-medium">@{entry.handle}</TableCell>
                  <TableCell className="text-muted-foreground max-w-md truncate">
                    {entry.reason}
                  </TableCell>
                  <TableCell>{entry.blocked_by.name}</TableCell>
                  <TableCell className="text-muted-foreground">
                    {dateFmt.format(new Date(entry.blocked_at))}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="inline-flex gap-1">
                      <BlocklistFormDialog
                        mode={{
                          kind: "edit",
                          entry,
                          trigger: (
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon"
                              disabled={!online}
                              aria-label={t("admin.blocklist.row.edit_label", {
                                handle: `@${entry.handle}`,
                              })}
                              data-testid="admin-blocklist-edit-button"
                              data-entry-id={entry.id}
                            >
                              <Pencil className="size-4" aria-hidden="true" />
                            </Button>
                          ),
                        }}
                      />
                      <RemoveConfirmDialog entry={entry} online={online} />
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </ListShell>

      {overwriteOpen && duplicate_found && (
        <BlocklistFormDialog
          mode={{
            kind: "overwrite",
            duplicate: duplicate_found,
            // On the SUCCESS path: Backend's PATCH redirects (303) back
            // to /admin/blocklist with fresh entries + flash; the
            // 303-driven page render replaces `duplicate_found` props
            // and fires `useFlash → sonner` once. We just close the
            // local overlay state.
            //
            // On the CANCEL path: `duplicate_found` stays in props (no
            // PATCH to clear it), but the local `overwriteOpen` is set
            // to false. The `useEffect([duplicate_found])` reference
            // equality keeps the dialog from re-mounting on subsequent
            // re-renders. The next add-attempt (POST) will arrive with
            // either a fresh `duplicate_found` (if collision) or
            // a 303 that drops the prop (if not).
            //
            // Previously this called `router.reload(...)` after close,
            // which re-fetched the same flash and double-fired the
            // toast (QA Lead's K12 polish flag). Removed.
            onClose: () => setOverwriteOpen(false),
          }}
        />
      )}
    </AppLayout>
  )
}

interface RemoveConfirmDialogProps {
  entry: BlocklistEntry
  online: boolean
}

function RemoveConfirmDialog({ entry, online }: RemoveConfirmDialogProps) {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  const [submitting, setSubmitting] = useState(false)

  const handleSubmit = () => {
    setSubmitting(true)
    router.delete(adminBlocklistEntryPath({ id: entry.id }), {
      preserveScroll: true,
      onFinish: () => {
        setSubmitting(false)
        setOpen(false)
      },
    })
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <Button
        type="button"
        variant="ghost"
        size="icon"
        disabled={!online}
        aria-label={t("admin.blocklist.row.remove_label", {
          handle: `@${entry.handle}`,
        })}
        onClick={() => setOpen(true)}
        data-testid="admin-blocklist-remove-button"
        data-entry-id={entry.id}
      >
        <Trash2 className="size-4" aria-hidden="true" />
      </Button>
      <DialogContent
        className="sm:max-w-md"
        data-testid="admin-blocklist-remove-dialog"
      >
        <DialogHeader>
          <DialogTitle>
            {t("admin.blocklist.remove_dialog.title", {
              handle: `@${entry.handle}`,
            })}
          </DialogTitle>
          <DialogDescription>
            {t("admin.blocklist.remove_dialog.body", {
              handle: `@${entry.handle}`,
            })}
          </DialogDescription>
        </DialogHeader>
        <DialogFooter className="gap-2 sm:gap-2">
          <Button
            type="button"
            variant="outline"
            autoFocus
            onClick={() => setOpen(false)}
          >
            {t("admin.blocklist.remove_dialog.cancel")}
          </Button>
          <Button
            type="button"
            variant="destructive"
            disabled={submitting || !online}
            aria-disabled={submitting || !online ? "true" : undefined}
            onClick={handleSubmit}
            data-testid="admin-blocklist-remove-confirm"
          >
            {t("admin.blocklist.remove_dialog.submit")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
